import { Box, Grid, Text } from "@chakra-ui/react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { PlaybackButton } from "@/components";
import { KNIT_ALONG, type KnitChapter } from "@/content";
import { usePlaybackToggle } from "@/hooks";
import { drawKnitmapStrip, loadKnitmap } from "./knitmap";
import {
  chapterAt,
  courseAt,
  holdingIn,
  knittingIn,
  loadTimeline,
  progressAt,
  secondsAt,
  stitchesAt,
  type KnitAlongTimeline,
} from "./timeline";

const PLAYHEAD = "#be4720";

const AHEAD_OPACITY = 0.22;

const MAX_PIXEL_RATIO = 2;

const PAGE_COURSES = 24;

type Loaded = {
  readonly timeline: KnitAlongTimeline;
  readonly knitmap: HTMLImageElement;
};

type Status = "idle" | "loading" | "ready" | "failed";

export type KnitViewerHandle = {
  readonly seek: (course: number) => void;
};

type KnitViewerProps = {
  readonly chapters: readonly KnitChapter[];
  readonly onChapterChange: (chapter: KnitChapter) => void;
};

const formatCount = (value: number) => value.toLocaleString("en-US");

// The Fabric view's render of Suzanne being knitted (a video), and beside it the knitmap the
// machine works through, with the readouts and chapters kept in step with the video.
export const KnitViewer = forwardRef<KnitViewerHandle, KnitViewerProps>(({ chapters, onChapterChange }, ref) => {
  const { isPlaying, toggle } = usePlaybackToggle();
  const [status, setStatus] = useState<Status>("idle");
  const [course, setCourse] = useState(0);
  const [isOnScreen, setIsOnScreen] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);

  const figureRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const stripRef = useRef<HTMLCanvasElement>(null);
  const stitchesRef = useRef<HTMLElement>(null);
  const loadedRef = useRef<Loaded | null>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const courseRef = useRef(-1);
  const chapterRef = useRef(chapters[0]);
  const onChapterChangeRef = useRef(onChapterChange);

  useEffect(() => {
    onChapterChangeRef.current = onChapterChange;
  });

  const seek = useCallback((target: number) => {
    const video = videoRef.current;
    const loaded = loadedRef.current;
    if (!video || !loaded) {
      pendingSeekRef.current = target;
      return;
    }
    video.currentTime = secondsAt(loaded.timeline, target);
  }, []);

  useImperativeHandle(ref, () => ({ seek }), [seek]);

  useEffect(() => {
    const figure = figureRef.current;
    if (!figure) return;
    if (stitchesRef.current) stitchesRef.current.textContent = "–";

    const nearby = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatus((current) => (current === "idle" ? "loading" : current));
      },
      { rootMargin: "400px 0px" },
    );
    const visible = new IntersectionObserver(([entry]) => setIsOnScreen(entry.isIntersecting), { threshold: 0.25 });
    nearby.observe(figure);
    visible.observe(figure);

    const handleVisibility = () => setIsPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      nearby.disconnect();
      visible.disconnect();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  useEffect(() => {
    if (status !== "loading") return;
    let cancelled = false;

    Promise.all([loadTimeline(KNIT_ALONG.data), loadKnitmap(KNIT_ALONG.knitmap)])
      .then(([timeline, knitmap]) => {
        if (cancelled) return;
        loadedRef.current = { timeline, knitmap };
        const pending = pendingSeekRef.current;
        if (pending !== null && videoRef.current) videoRef.current.currentTime = secondsAt(timeline, pending);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });

    return () => {
      cancelled = true;
    };
  }, [status]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying && isOnScreen && isPageVisible) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isPlaying, isOnScreen, isPageVisible]);

  useEffect(() => {
    if (status !== "ready" || !isOnScreen || !isPageVisible) return;
    const video = videoRef.current;
    const strip = stripRef.current;
    const loaded = loadedRef.current;
    if (!video || !strip || !loaded) return;
    const { timeline, knitmap } = loaded;
    const context = strip.getContext("2d");
    let drawn = { time: -1, width: 0, height: 0 };

    let frame = requestAnimationFrame(function tick() {
      const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      const width = Math.max(1, Math.round(strip.clientWidth * ratio));
      const height = Math.max(1, Math.round(strip.clientHeight * ratio));
      const time = video.currentTime;

      if (time !== drawn.time || width !== drawn.width || height !== drawn.height) {
        drawn = { time, width, height };
        const progress = progressAt(timeline, time);
        if (strip.width !== width || strip.height !== height) {
          strip.width = width;
          strip.height = height;
        }
        if (context) {
          drawKnitmapStrip(context, knitmap, { width, height, progress, ahead: AHEAD_OPACITY, playhead: PLAYHEAD });
        }
        if (stitchesRef.current) stitchesRef.current.textContent = formatCount(stitchesAt(timeline, progress));
        const row = progress >= timeline.courses ? timeline.courses : courseAt(timeline, progress);
        if (row !== courseRef.current) {
          courseRef.current = row;
          setCourse(row);
          const chapter = chapterAt(chapters, Math.min(row, timeline.courses - 1));
          if (chapter !== chapterRef.current) {
            chapterRef.current = chapter;
            onChapterChangeRef.current(chapter);
          }
        }
      }

      frame = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(frame);
  }, [status, isOnScreen, isPageVisible, chapters]);

  const pause = useCallback(() => {
    if (isPlaying) toggle();
  }, [isPlaying, toggle]);

  const seekToPointer = (event: PointerEvent<HTMLCanvasElement>) => {
    const loaded = loadedRef.current;
    if (!loaded) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const fraction = Math.min(Math.max(1 - (event.clientY - bounds.top) / bounds.height, 0), 1);
    seek(Math.round(fraction * loaded.timeline.courses));
  };

  const handleStripPointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pause();
    seekToPointer(event);
  };

  const handleStripPointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) seekToPointer(event);
  };

  const handleStripKeyDown = (event: KeyboardEvent<HTMLCanvasElement>) => {
    const loaded = loadedRef.current;
    if (!loaded) return;
    const { courses } = loaded.timeline;
    const steps: Record<string, number> = {
      ArrowUp: course + 1,
      ArrowRight: course + 1,
      ArrowDown: course - 1,
      ArrowLeft: course - 1,
      PageUp: course + PAGE_COURSES,
      PageDown: course - PAGE_COURSES,
      Home: 0,
      End: courses,
    };
    if (!(event.key in steps)) return;
    event.preventDefault();
    pause();
    seek(Math.min(Math.max(steps[event.key], 0), courses));
  };

  const timeline = status === "ready" ? (loadedRef.current?.timeline ?? null) : null;
  const courses = timeline?.courses ?? KNIT_ALONG.courses;
  const isFinished = course >= courses;
  const shownCourse = Math.min(course, courses - 1);
  const chapter = chapterAt(chapters, shownCourse);
  const chapterNumber = String(chapters.indexOf(chapter) + 1).padStart(2, "0");

  const readouts = [
    {
      id: "course",
      label: KNIT_ALONG.readouts.course,
      value: timeline ? `${formatCount(shownCourse + 1)} / ${formatCount(courses)}` : "–",
    },
    { id: "stitches", label: KNIT_ALONG.readouts.stitches, value: null },
    {
      id: "knitting",
      label: KNIT_ALONG.readouts.knitting,
      value: timeline && !isFinished ? formatCount(knittingIn(timeline, shownCourse)) : "–",
    },
    {
      id: "holding",
      label: KNIT_ALONG.readouts.holding,
      value: timeline && !isFinished ? formatCount(holdingIn(timeline, shownCourse)) : "–",
    },
  ];

  return (
    <Box
      ref={figureRef}
      as="figure"
      className="print-avoid-break"
      borderRadius="lg"
      overflow="hidden"
      bg="surface.white"
      border="1px solid"
      borderColor="line.hairline"
    >
      <Grid templateColumns="minmax(0, 1fr) clamp(56px, 8vw, 96px)">
        <Box position="relative" minW={0} sx={{ aspectRatio: { base: "1 / 1", md: "5 / 4" } }}>
          <video
            ref={videoRef}
            src={KNIT_ALONG.video.src}
            poster={KNIT_ALONG.video.poster}
            width={KNIT_ALONG.video.width}
            height={KNIT_ALONG.video.height}
            aria-label={`${KNIT_ALONG.label}. ${KNIT_ALONG.description}`}
            muted
            loop
            playsInline
            preload="none"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
          />

          <Text position="absolute" top="18px" left="18px" right="72px" variant="eyebrowBrand" pointerEvents="none">
            {chapterNumber} · {chapter.title}
          </Text>

          {status === "failed" && (
            <Text position="absolute" left="18px" right="18px" bottom="14px" variant="small" pointerEvents="none">
              {KNIT_ALONG.failed}
            </Text>
          )}

          <PlaybackButton isPlaying={isPlaying} onToggle={toggle} />
        </Box>

        <Grid
          templateRows="auto minmax(0, 1fr)"
          borderLeft="1px solid"
          borderColor="line.hairline"
          bg="surface.DEFAULT"
        >
          <Text variant="eyebrow" textAlign="center" pt="20px" pb="12px" fontSize="0.625rem" letterSpacing="0.1em">
            {KNIT_ALONG.stripLabel}
          </Text>
          <Box position="relative" mx="clamp(6px, 1vw, 12px)" mb="14px">
            <canvas
              ref={stripRef}
              role="slider"
              tabIndex={0}
              aria-label={KNIT_ALONG.sliderLabel}
              aria-orientation="vertical"
              aria-valuemin={1}
              aria-valuemax={courses}
              aria-valuenow={Math.max(Math.min(course + 1, courses), 1)}
              aria-valuetext={`Course ${formatCount(shownCourse + 1)} of ${formatCount(courses)}: ${chapter.title}`}
              onPointerDown={handleStripPointerDown}
              onPointerMove={handleStripPointerMove}
              onKeyDown={handleStripKeyDown}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                touchAction: "none",
                cursor: "ns-resize",
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid
        as="dl"
        templateColumns={{ base: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }}
        borderTop="1px solid"
        borderColor="line.hairline"
      >
        {readouts.map((readout, index) => (
          <Box
            key={readout.id}
            px="18px"
            py="14px"
            borderLeft={{ base: index % 2 ? "1px solid" : "none", md: index ? "1px solid" : "none" }}
            borderTop={{ base: index > 1 ? "1px solid" : "none", md: "none" }}
            borderColor="line.hairline"
          >
            <Text as="dt" variant="eyebrow" fontSize="0.625rem" letterSpacing="0.1em">
              {readout.label}
            </Text>
            {/* The stitch count changes every frame, so the loop above writes it directly. */}
            <Text
              as="dd"
              ref={readout.value === null ? stitchesRef : undefined}
              mt="6px"
              fontSize="lede"
              lineHeight={1}
              color="ink.DEFAULT"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {readout.value}
            </Text>
          </Box>
        ))}
      </Grid>
    </Box>
  );
});

KnitViewer.displayName = "KnitViewer";
